import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import CreateThemeModal from "@/components/modals/CreateThemeModal";
import { postBrand } from "@/services/setting.service";
import { BUTTONS, HEADINGS, LABELS, STATUS, TEXT } from "@/utils/constants/appConstants/commonConstants";
import {
    BRAND_CREATED_SUCCESSFULLY,
    BRAND_NAME_REQUIRED,
    COMPANY_NAME_REQUIRED,
    FILE_TYPE_ERROR,
    REQUIRED_ALL_FIELDS,
    SOMETHING_WENT_WRONG,
} from "@/utils/constants/messages";
import { convertBase64 } from "@/utils/functions/helperFunctions";
import { errorToasterMessage } from "@/utils/functions/toaster";
import { Button } from "@/components/ui/button";
import { ButtonLoadingContent, loadingButtonClass } from "@/components/buttons/ButtonLoadingContent";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { DEFAULT_THEME_COLORS } from "@/utils/constants/colors";
import { prependListCacheItem } from "@/utils/listPageCache";
import { cn } from "@/lib/utils";

type BrandFormItemProps = {
    id: string;
    label: string;
    labelClassName?: string;
    endAlign?: boolean;
    children: React.ReactNode;
};

function BrandFormItem({ id, label, labelClassName, endAlign = false, children }: BrandFormItemProps) {
    return (
        <div className="gs-brand-form-item">
            <div className={cn("gs-brand-form-col", endAlign && "gs-brand-form-col--end")}>
                <div id={`${id}-label`} className={cn("gs-brand-form-label", labelClassName)}>
                    {label}
                </div>
                <div id={`${id}-value`} className="gs-brand-form-value">
                    {children}
                </div>
            </div>
        </div>
    );
}

const AddBrand = () => {
    const [headerBg, setHeaderBg] = useState(DEFAULT_THEME_COLORS.headerBg);
    const [headerText, setHeaderText] = useState(DEFAULT_THEME_COLORS.headerText);
    const [buttonBg, setButtonBg] = useState(DEFAULT_THEME_COLORS.buttonBg);
    const [buttonText, setButtonText] = useState(DEFAULT_THEME_COLORS.buttonText);
    const [brandName, setBrandName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [status, setStatus] = useState("");
    const [brandMsg, setBrandMsg] = useState("");
    const [brandErrMsg, setBrandErrMsg] = useState("");
    const [companyErrMsg, setCompanyErrMsg] = useState("");
    const [errMsg, setErrMsg] = useState("");
    const [showThemeModal, setShowThemeModal] = React.useState(false);
    const [emailFile, setEmailFile] = useState<any>();
    const [signingFile, setSigningFile] = useState<any>();
    const [emailImage, setEmailImage] = useState<any>();
    const [signingImage, setSigningImage] = useState<any>();
    const [isBrandLoading, setIsBrandLoading] = useState(false);
    const navigate = useNavigate();

    const onFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0].type.startsWith("image/")) {
            errorToasterMessage(FILE_TYPE_ERROR);
            return;
        }
        const _files = await convertBase64(e.target.files[0]);
        setEmailFile(_files);
        setEmailImage(e.target.files[0]);
    };

    const onFileSelect2 = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0].type.startsWith("image/")) {
            errorToasterMessage(FILE_TYPE_ERROR);
            return;
        }
        const _files = await convertBase64(e.target.files[0]);
        setSigningFile(_files);
        setSigningImage(e.target.files[0]);
    };

    const handleBrandNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setBrandErrMsg("");
        setBrandName(e.target.value);
    };

    const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCompanyErrMsg("");
        setCompanyName(e.target.value);
    };

    const handleCreateBrand = () => {
        setErrMsg("");
        setBrandMsg("");
        const data = JSON.stringify({
            is_active: status === "active",
            name: brandName,
            company_name: companyName,
            colors: {
                headers: { background: headerBg, text: headerText },
                buttons: { background: buttonBg, text: buttonText },
            },
            logos: { email: emailFile, signing: signingFile },
        });
        if (brandName.trim() === "" && companyName.trim() === "") {
            setBrandErrMsg(BRAND_NAME_REQUIRED);
            setCompanyErrMsg(COMPANY_NAME_REQUIRED);
            return;
        }
        if (brandName.trim() === "") {
            setBrandErrMsg(BRAND_NAME_REQUIRED);
            return;
        }
        if (companyName.trim() === "") {
            setCompanyErrMsg(COMPANY_NAME_REQUIRED);
            return;
        }
        if (!emailFile || !signingFile) {
            setErrMsg(REQUIRED_ALL_FIELDS);
            return;
        }

        setIsBrandLoading(true);
        postBrand(
            data,
            (response: any) => {
                setIsBrandLoading(false);
                if (response.status >= 200 && response.status < 300) {
                    const createdBrand =
                        response.data?.branding ??
                        response.data?.brand ??
                        response.data?.data ??
                        response.data;
                    if (createdBrand && typeof createdBrand === "object") {
                        prependListCacheItem("brands", {}, {
                            ...createdBrand,
                            name: createdBrand.name ?? brandName,
                            company_name: createdBrand.company_name ?? companyName,
                            is_active: createdBrand.is_active ?? status === "active",
                        });
                    }
                    setBrandErrMsg("");
                    setBrandMsg(BRAND_CREATED_SUCCESSFULLY);
                    setCompanyName("");
                    setBrandName("");
                    setErrMsg("");
                    setEmailFile(null);
                    navigate("/brands");
                } else {
                    setBrandMsg(SOMETHING_WENT_WRONG);
                }
            },
            (error: any) => {
                setIsBrandLoading(false);
                setBrandMsg("");
                setErrMsg(error.message);
            }
        );
    };

    return (
        <Card className="gs-brand-form-card gs-list-page-card gap-0 py-0 shadow-none">
            <div className="gs-brand-form-inner">
                <h4 className="gs-brand-form-title" id="add-brand-heading">
                    {HEADINGS.BRANDING}
                </h4>

                <form onSubmit={(event) => event.preventDefault()}>
                    <div className="gs-brand-form-head">
                        <h6 className="gs-brand-form-head-title" id="add-brand-info">
                            {HEADINGS.INFO}
                        </h6>
                    </div>

                    <div className="gs-brand-form-group">
                        <BrandFormItem id="create-brand-name" label={LABELS.BRAND_NAME}>
                            <div>
                                <Input
                                    autoFocus
                                    type="text"
                                    onChange={handleBrandNameChange}
                                    value={brandName}
                                    className="gs-brand-form-input"
                                    id="create-brand-name-input"
                                />
                                {brandErrMsg && (
                                    <p className="gs-brand-form-field-error" id="create-brand-name-error">
                                        {brandErrMsg}
                                    </p>
                                )}
                            </div>
                        </BrandFormItem>

                        <BrandFormItem id="create-brand-company-name" label={LABELS.COMPANY_NAME}>
                            <div>
                                <Input
                                    type="text"
                                    onChange={handleCompanyNameChange}
                                    value={companyName}
                                    className="gs-brand-form-input"
                                    id="create-brand-company-name-input"
                                />
                                {companyErrMsg && (
                                    <p
                                        className="gs-brand-form-field-error"
                                        id="create-brand-company-name-error"
                                    >
                                        {companyErrMsg}
                                    </p>
                                )}
                            </div>
                        </BrandFormItem>

                        <BrandFormItem id="create-brand-status" label={LABELS.STATUS}>
                            <Select value={status || undefined} onValueChange={setStatus}>
                                <SelectTrigger
                                    id="create-brand-status-trigger"
                                    className="gs-brand-form-select"
                                >
                                    <SelectValue placeholder="Status" />
                                </SelectTrigger>
                                <SelectContent
                                    position="popper"
                                    sideOffset={1}
                                    align="start"
                                    className="gs-brand-form-select-content"
                                >
                                    {STATUS.map((option) => (
                                        <SelectItem
                                            key={option.value}
                                            value={option.value}
                                            className="gs-brand-form-select-item !flex !min-h-[2.125rem] !items-center !rounded-none !py-2.5 !pl-4 !pr-4 !text-[0.8125rem] !leading-5 data-[highlighted]:!bg-surface-hover data-[highlighted]:!text-primary"
                                        >
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </BrandFormItem>
                    </div>
                </form>

                <div className="gs-brand-form-section">
                    <div className="gs-brand-form-head">
                        <h6 className="gs-brand-form-head-title" id="create-brand-customize-recipient-heading">
                            {HEADINGS.CUSTOMIZE_WHAT_RECIPIENT_SEE}
                        </h6>
                    </div>

                    <BrandFormItem
                        id="brand-create-theme-btn"
                        label={TEXT.ADD_LOGO_AND_BRAND}
                        endAlign
                    >
                        <Button
                            id="add-brand-create-theme-btn"
                            type="button"
                            onClick={() => setShowThemeModal(true)}
                        >
                            {BUTTONS.CREATE_YOUR_THEME}
                        </Button>
                    </BrandFormItem>
                </div>

                {errMsg && (
                    <p id="create-brand-error-msg" className="gs-brand-form-msg gs-brand-form-msg--error">
                        {errMsg}
                    </p>
                )}
                {brandMsg && (
                    <p id="create-brand-success-msg" className="gs-brand-form-msg gs-brand-form-msg--success">
                        {brandMsg}
                    </p>
                )}

                <div className="gs-brand-form-actions">
                    <Button
                        id="brand-save-btn"
                        type="submit"
                        className={loadingButtonClass(isBrandLoading)}
                        disabled={isBrandLoading}
                        aria-busy={isBrandLoading || undefined}
                        onClick={handleCreateBrand}
                    >
                        <ButtonLoadingContent loading={isBrandLoading} loaderId="brand-save-btn-loader">
                            {BUTTONS.SAVE}
                        </ButtonLoadingContent>
                    </Button>
                </div>
            </div>

            <CreateThemeModal
                show={showThemeModal}
                onHide={() => setShowThemeModal(false)}
                emailFile={emailFile}
                signingFile={signingFile}
                emailImage={emailImage}
                signingImage={signingImage}
                onFileSelect={onFileSelect}
                onFileSelect2={onFileSelect2}
                headerBg={headerBg}
                setHeaderBg={setHeaderBg}
                headerText={headerText}
                setHeaderText={setHeaderText}
                buttonBg={buttonBg}
                setButtonBg={setButtonBg}
                buttonText={buttonText}
                setButtonText={setButtonText}
            />
        </Card>
    );
};

export default AddBrand;
